'use client';

import './form.css';

import * as Sentry from '@sentry/react';
import classnames from 'classnames';
import * as EmailValidator from 'email-validator';
import { usePathname } from 'next/navigation';
import { useRef, useState } from 'react';

import { Button } from '@/components/button';
import { PrimaryCheck } from '@/components/icons';

import { render } from '@/helpers/storyblok/rich-text';

const STATE = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

export const Form = ({ blok }) => {
  const pathname = usePathname();

  const formRef = useRef(null);

  const [state, setState] = useState(STATE.IDLE);

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setState(STATE.LOADING);

    const data = {
      site: 'Unlock-M',
      form: blok.id,
      firstName,
      lastName,
      company,
      linkedin,
      email,
      message,
      brevoListId: blok.brevoListId,
      websiteSource: pathname,
    };

    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    };

    try {
      const response = await fetch('/api/form', options);
      if (!response.ok) {
        throw new Error(`${blok.id} form submit error`);
      }
      setState(STATE.SUCCESS);
      Sentry.withScope((scope) => {
        scope.setExtras(data);
        Sentry.captureMessage('Form submitted successfully');
      });
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setExtras(data);
        Sentry.captureException(error);
      });
      setState(STATE.ERROR);
    }
  };

  const getSplitClass = (fields) => {
    const fieldCount = Object.values(fields).reduce(
      (count, field) => (field ? ((count as number) || 0) + 1 : count),
      0,
    );
    return classnames('contact-form__split', {
      'contact-form__split--flex': fieldCount === 1,
    });
  };

  return (
    <>
      <div
        className={classnames('contact-form', {
          'contact-form--state-loading': state === STATE.LOADING,
          'contact-form--state-success': state === STATE.SUCCESS,
          'contact-form--state-error': state === STATE.ERROR,
        })}
      >
        {blok.title ? (
          <div className="contact-form__title">{render(blok.title)}</div>
        ) : null}

        <form onSubmit={handleSubmit} ref={formRef}>
          {blok.lastNameLabel || blok.firstNameLabel ? (
            <div
              className={getSplitClass({
                lastName: blok.lastNameLabel,
                firstName: blok.firstNameLabel,
              })}
            >
              {blok.lastNameLabel ? (
                <div className="contact-form__input-container">
                  <input
                    className="contact-form__input"
                    type="text"
                    name="lastName"
                    placeholder={blok.lastNameLabel}
                    autoComplete="lastName"
                    value={lastName}
                    onInput={(event) => setLastName(event.target.value)}
                    readOnly={state === STATE.LOADING}
                  />
                </div>
              ) : null}
              {blok.firstNameLabel ? (
                <div className="contact-form__input-container">
                  <input
                    className="contact-form__input"
                    type="text"
                    name="firstName"
                    placeholder={blok.firstNameLabel}
                    autoComplete="firstName"
                    value={firstName}
                    onInput={(event) => setFirstName(event.target.value)}
                    readOnly={state === STATE.LOADING}
                  />
                </div>
              ) : null}
            </div>
          ) : null}
          {blok.emailLabel || blok.companyLabel ? (
            <div
              className={getSplitClass({
                name: blok.emailLabel,
                company: blok.companyLabel,
              })}
            >
              {blok.emailLabel ? (
                <div className="contact-form__input-container">
                  <input
                    className="contact-form__input"
                    type="email"
                    name="email"
                    placeholder={blok.emailLabel}
                    autoComplete="email"
                    value={email}
                    onInput={(event) => setEmail(event.target.value)}
                    readOnly={state === STATE.LOADING}
                  />
                </div>
              ) : null}
              {blok.companyLabel ? (
                <div className="contact-form__input-container">
                  <input
                    className="contact-form__input"
                    type="text"
                    name="company"
                    placeholder={blok.companyLabel}
                    autoComplete="company"
                    value={company}
                    onInput={(event) => setCompany(event.target.value)}
                    readOnly={state === STATE.LOADING}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="contact-form__input-container">
            <input
              className="contact-form__input"
              type="url"
              name="linkedin"
              placeholder="URL LinkedIn"
              autoComplete="url"
              value={linkedin}
              onInput={(event) => setLinkedin(event.target.value)}
              readOnly={state === STATE.LOADING}
            />
          </div>

          {blok.messageLabel ? (
            <div className="contact-form__input-container-textarea">
              <textarea
                className="contact-form__textarea"
                name="message"
                placeholder={blok.messageLabel}
                autoComplete="no"
                value={message}
                onInput={(event) => setMessage(event.target.value)}
                readOnly={state === STATE.LOADING}
              />
            </div>
          ) : null}

          {state === STATE.ERROR ? (
            <div className="contact-form__error">
              {render(blok.errorMessage)}
            </div>
          ) : null}

          <div className="contact-form__cta">
            <Button
              theme="primary"
              disabled={
                [STATE.SUCCESS].includes(state) ||
                message === '' ||
                email === '' ||
                EmailValidator.validate(email) === false
              }
              loading={[STATE.LOADING].includes(state)}
            >
              {blok.submitLabel}
            </Button>
          </div>
        </form>
        {state === STATE.SUCCESS ? (
          <div className="contact-form__success">
            <div className="contact-form__success__icon">
              <PrimaryCheck />
            </div>
            {blok.successMessage ? (
              <div className="contact-form__success__message">
                {render(blok.successMessage)}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
};
